// Board — the Incidents section's kanban of runs, a sidebar sibling of
// Inbox and Coordinator. Moved here from `/triage/runs` in the 2026-07 nav
// review (the old URL redirects). Card click opens the run-detail dialog
// (DEV-16210) instead of navigating, so the board (filters, scroll
// position, polling) stays mounted while the operator inspects a single
// run. URL stays shareable: `?runId=<id>` toggles the dialog; bookmarking
// `/triage/runs/<id>` redirects into the same state via
// `routes/triage/runs/$runId.tsx`.
//
// DEV-17093: there used to be an Outcome/Status toggle here. It's gone —
// the status columns duplicated what the status filter dropdown already
// does, so there's now one board and the search box leads the toolbar.
// The column set (lifecycle spine + per-workflow custom columns) lives in
// `triage-columns.ts`; see the header comment there for the bucketing
// rules.
//
// File scope on purpose: filter state + list query + kanban composition.
// Subordinate concerns live in siblings:
//   - RunCard / RunDetailDialog            → components/runs/
//   - column model + run bucketing         → components/runs/triage-columns.ts
//   - search-param coercion + URL builder  → components/runs/triage-helpers.ts
import { useClient } from '@cortex/react-query';
import type { BoardColumn, IPaginated, Run, Workflow } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, type CSSProperties } from 'react';

import { Kanban } from '../../components/common/Kanban';
import { PageHeader } from '../../components/common/PageHeader';
import { RunCard } from '../../components/runs/RunCard';
import { RunDetailDialog } from '../../components/runs/RunDetailDialog';
import { RunFilters, type IRunFilterState } from '../../components/runs/RunFilters';
import { buildBoardLayout, resolveRunColumnKey } from '../../components/runs/triage-columns';
import {
  buildRunsUrl,
  parseTriageSearch,
  type ITriageSearch,
} from '../../components/runs/triage-helpers';
import { routes, terminalRunStatuses } from '../../lib/constants';

export const Route = createFileRoute('/inbox/board')({
  component: RunsTriagePage,
  validateSearch: parseTriageSearch,
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function RunsTriagePage() {
  const client = useClient();
  const nav = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  // The run-detail dialog is URL-driven — `?runId=<id>` opens it. That
  // makes deep-linking trivial (and `$runId.tsx` just redirects into
  // this same state). Closing the dialog clears the param.
  const openRunId = search.runId ?? null;

  // Filter state passed down to RunFilters / buildRunsUrl — excludes
  // `runId` so the list query key stays stable when the user opens the
  // dialog.
  const filters: IRunFilterState = useMemo(() => {
    const out: IRunFilterState = {};
    if (search.status) out.status = search.status;
    if (search.workflowId) out.workflowId = search.workflowId;
    if (search.trigger) out.trigger = search.trigger;
    if (search.since) out.since = search.since;
    if (search.until) out.until = search.until;
    return out;
  }, [search]);

  // URL mutator helpers — every nav call merges through `replace: true`
  // so the back button still pops out of the triage board cleanly (we
  // don't want each filter tweak to seed a history entry).
  const navWith = (extra: Partial<ITriageSearch>) =>
    fork(
      nav({
        search: {
          ...filters,
          ...(search.q ? { q: search.q } : {}),
          ...(openRunId ? { runId: openRunId } : {}),
          ...extra,
        },
        replace: true,
      }),
    );

  const setSearchQuery = (q: string) => navWith({ q: q.trim() ? q : undefined });
  const openRun = (id: string) => navWith({ runId: id });
  const closeRun = () => navWith({ runId: undefined });

  // Workflow list drives the filter dropdown AND the card title lookup
  // — one cached fetch covers both.
  const workflowsQuery = useQuery({
    queryKey: ['workflows', 'name-lookup'],
    queryFn: () =>
      client.http.get('v1/workflows').json<IListEnvelope<Pick<Workflow, 'id' | 'name'>>>(),
    staleTime: 60_000,
  });
  const workflowNamesById = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const w of workflowsQuery.data?.items ?? []) out[w.id] = w.name;
    return out;
  }, [workflowsQuery.data]);

  // Triage list: BE GET /v1/runs accepts status / workflowId / trigger /
  // since / until as independent ANDed filters (DEV-15906 / EU-39). Poll
  // cadence is dynamic — 2 s while any run is non-terminal, 30 s when
  // everything is settled. (DEV-16182.)
  const list = useQuery({
    queryKey: ['runs', 'triage', filters],
    queryFn: () => client.http.get(buildRunsUrl(filters)).json<IListEnvelope<Run>>(),
    refetchInterval: (query) => {
      const data = query.state.data as IListEnvelope<Run> | undefined;
      const hasActive = (data?.items ?? []).some(
        (r) => !(terminalRunStatuses as ReadonlySet<string>).has(r.status),
      );
      return hasActive ? 2000 : 30000;
    },
  });

  // Board columns — a column set only reads as a column set in the context
  // of one workflow's graph, so we only fetch (and only render) custom
  // columns once a workflow filter is on. `?workflowId=` makes the server
  // derive the set from that workflow's current-version edge tags
  // (DEV-17093). The unfiltered cache key stays shared with the admin page
  // + edge inspector.
  const selectedWorkflowId = filters.workflowId;
  const boardColumnsQuery = useQuery({
    enabled: !!selectedWorkflowId,
    queryKey: ['board-columns', { workflowId: selectedWorkflowId ?? null }],
    queryFn: () =>
      client.http
        .get(
          `v1/board-columns?limit=500&workflowId=${encodeURIComponent(selectedWorkflowId ?? '')}`,
        )
        .json<IPaginated<BoardColumn>>(),
  });

  const layout = useMemo(
    () =>
      buildBoardLayout({
        workflowSelected: !!selectedWorkflowId,
        customColumns: boardColumnsQuery.data?.items,
      }),
    [selectedWorkflowId, boardColumnsQuery.data],
  );

  const getColumnKey = useMemo(() => (r: Run) => resolveRunColumnKey(r, layout), [layout]);

  // Client-side text-search filter — matches workflow name (resolved),
  // run id, status, and error message. Lowercased + trimmed. Empty
  // query passes everything through. (DEV-16180.)
  const filteredItems = useMemo(() => {
    const items = list.data?.items ?? [];
    const q = (search.q ?? '').trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      const name = workflowNamesById[r.workflowId] ?? '';
      const errMsg =
        r.error && typeof r.error === 'object' && 'message' in r.error
          ? String((r.error as { message?: unknown }).message ?? '')
          : '';
      return (
        name.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q) ||
        errMsg.toLowerCase().includes(q)
      );
    });
  }, [list.data, search.q, workflowNamesById]);

  return (
    <>
      <PageHeader title="Board" />
      <div style={toolbarRow}>
        <input
          type="search"
          className="input"
          placeholder="Search runs (workflow, id, error message)…"
          aria-label="Search runs"
          value={search.q ?? ''}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={searchInputStyle}
        />
        <RunFilters
          value={filters}
          onChange={(next) =>
            fork(
              nav({
                search: {
                  ...next,
                  ...(search.q ? { q: search.q } : {}),
                  ...(openRunId ? { runId: openRunId } : {}),
                },
                replace: true,
              }),
            )
          }
          workflows={workflowsQuery.data?.items ?? []}
          isWorkflowsLoading={workflowsQuery.isLoading}
        />
        <Link to={routes.BOARD_COLUMNS} className="btn btn--ghost btn--sm">
          Manage columns
        </Link>
        {/*
          Two different nudges, because "no columns" means different things
          on either side of the workflow filter. Unfiltered, the custom
          columns are collapsed by design and the fix is to pick a workflow.
          Filtered, an empty set means that workflow's graph has no edge
          attribution yet — which is a real gap worth pointing at.
        */}
        {!selectedWorkflowId ? (
          <span className="t-xs t-secondary">Select a workflow to see its outcome columns.</span>
        ) : !boardColumnsQuery.isLoading && (boardColumnsQuery.data?.items ?? []).length === 0 ? (
          <span className="t-xs t-secondary">
            This workflow has no outcome columns —{' '}
            <Link to={routes.BOARD_COLUMNS} style={{ color: 'var(--text-secondary)' }}>
              create one
            </Link>{' '}
            and tag an edge with it in the workflow builder.
          </span>
        ) : null}
      </div>
      <Kanban<Run>
        key={selectedWorkflowId ?? '__all'}
        columns={layout.columns}
        items={filteredItems}
        isLoading={list.isLoading}
        error={list.error as Error | null}
        getColumnKey={getColumnKey}
        getItemKey={(r) => r.id}
        renderCard={(r) => (
          <RunCard
            run={r}
            workflowName={workflowNamesById[r.workflowId]}
            onOpen={() => openRun(r.id)}
          />
        )}
      />
      <RunDetailDialog runId={openRunId} open={!!openRunId} onClose={closeRun} />
    </>
  );
}

// Single toolbar row that hosts the mode toggle, free-text search, and
// the filter group. flex-wrap lets the filters drop to a second visual
// row on narrow viewports without us owning a separate layout — one
// row when we have the space, two when we don't. (DEV-16182.)
const toolbarRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3)',
  flexWrap: 'wrap',
  padding: '0 var(--sp-3) var(--sp-2) var(--sp-3)',
};

const searchInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 200,
  maxWidth: 360,
  height: 30,
  fontSize: 'var(--fs-sm)',
};
